/**
 * Error Handler Utility
 *
 * Classifies and provides user-friendly error messages for fetch/API errors.
 * Helps users understand what went wrong and how to fix it.
 */

/**
 * Classifies a fetch error and returns structured error information
 * @param {Error} error - The error object from fetch
 * @param {Response} response - The response object (if available)
 * @param {Object} data - The parsed response data (if available)
 * @returns {Object} Structured error information with type, title, message, and suggestions
 */
export const classifyFetchError = (error, response = null, data = null) => {
  // Server unreachable / network error
  if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
    return {
      type: 'CONNECTION_FAILED',
      title: 'Unable to connect to server',
      message: 'The server appears to be offline or unreachable.',
      suggestions: [
        'Make sure the server is running: npm run server',
        'Verify the server is accessible at localhost:3001',
        'Check your network connection'
      ],
      retryable: true,
      duration: 6000 // Longer duration for important errors
    };
  }

  // Timeout
  if (error.name === 'AbortError') {
    return {
      type: 'TIMEOUT',
      title: 'Request timed out',
      message: 'The server took too long to respond (60s timeout).',
      suggestions: [
        'Check if the server is running',
        'The API might be experiencing high load',
        'Try again in a moment'
      ],
      retryable: true,
      duration: 5000
    };
  }

  // Claude API - Insufficient credits
  if (response?.status === 429 && data?.error?.type === 'rate_limit_error') {
    // Check if it's specifically about credits or rate limits
    const errorMessage = data?.error?.message || '';
    const isCreditsError = errorMessage.toLowerCase().includes('credit') ||
                          errorMessage.toLowerCase().includes('quota') ||
                          errorMessage.toLowerCase().includes('billing');

    if (isCreditsError) {
      return {
        type: 'INSUFFICIENT_CREDITS',
        title: 'AI credits exhausted',
        message: 'We\'re temporarily out of AI credits. Please try again later.',
        suggestions: [
          'The server needs more Anthropic API credits',
          'Contact the administrator to add credits',
          'Try again in a few hours'
        ],
        retryable: false,
        disableFor: 15 * 60 * 1000, // Disable for 15 minutes
        duration: 8000
      };
    }

    return {
      type: 'RATE_LIMITED_API',
      title: 'Claude API rate limit',
      message: 'The AI service is rate limited. Please wait before trying again.',
      suggestions: [
        'Wait a few minutes before trying again',
        'The API has request limits per minute'
      ],
      retryable: false,
      disableFor: 5 * 60 * 1000, // Disable for 5 minutes
      duration: 6000
    };
  }

  // Claude API - Overloaded
  if (response?.status === 529 || data?.error?.type === 'overloaded_error') {
    return {
      type: 'API_OVERLOADED',
      title: 'AI service overloaded',
      message: 'The Claude API is currently overloaded. Please try again shortly.',
      suggestions: [
        'Wait a few minutes and try again',
        'The service is experiencing high demand',
        'Your request will work once capacity is available'
      ],
      retryable: true,
      disableFor: 2 * 60 * 1000, // Disable for 2 minutes
      duration: 6000
    };
  }

  // Rate limiting (local server)
  if (response?.status === 429) {
    return {
      type: 'RATE_LIMITED',
      title: 'Too many requests',
      message: 'You\'ve reached the rate limit. Please wait before trying again.',
      suggestions: [
        'Wait 15 minutes before generating more patterns',
        'Rate limit: 20 requests per 15 minutes per IP'
      ],
      retryable: false,
      disableFor: 15 * 60 * 1000, // Disable for 15 minutes
      duration: 6000
    };
  }

  // Bad request
  if (response?.status === 400) {
    return {
      type: 'BAD_REQUEST',
      title: 'Invalid request',
      message: error.message || 'The request was invalid.',
      suggestions: [
        'Check your prompt length (max 500 characters)',
        'Make sure your prompt is not empty'
      ],
      retryable: true,
      duration: 5000
    };
  }

  // Server error
  if (response?.status >= 500) {
    return {
      type: 'SERVER_ERROR',
      title: 'Server error',
      message: 'The server encountered an error while processing your request.',
      suggestions: [
        'Check server logs for details',
        'Verify your Anthropic API key is configured',
        'Try again in a moment'
      ],
      retryable: true,
      duration: 5000
    };
  }

  // API error (4xx)
  if (response?.status >= 400) {
    return {
      type: 'API_ERROR',
      title: 'Generation failed',
      message: error.message || 'An error occurred while generating the pattern.',
      suggestions: [
        'Try rephrasing your prompt',
        'Check server logs for details'
      ],
      retryable: true,
      duration: 5000
    };
  }

  // Generic error
  return {
    type: 'UNKNOWN',
    title: 'Something went wrong',
    message: error.message || 'An unexpected error occurred.',
    suggestions: ['Please try again'],
    retryable: true,
    duration: 4000
  };
};

/**
 * Formats error information into a user-friendly message
 * @param {Object} errorInfo - Structured error information from classifyFetchError
 * @param {boolean} includeDetails - Whether to include suggestions
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (errorInfo, includeDetails = false) => {
  let message = `${errorInfo.title}: ${errorInfo.message}`;

  if (includeDetails && errorInfo.suggestions.length > 0) {
    message += '\n\nSuggestions:\n';
    message += errorInfo.suggestions.map(s => `• ${s}`).join('\n');
  }

  return message;
};
